import * as React from 'react';
import {useCallback, useEffect, useLayoutEffect, useState} from 'react';
import {useKeyCommands} from '../../../shared-common/hooks/useKeyCommands';
import {useFloatingPosition, FloatingPlacement} from '../../hooks/useFloatingPosition';
import {useTopLayer} from '../../hooks/useTopLayer';
import ReactDOM from 'react-dom';
import styles from './TooltipProvider.module.css';
import {isNil} from 'lodash-es';
import Helpers from '../../../shared-common/helpers/Helpers';

export interface TooltipProviderProps {
  delayMilliseconds?: number;
}

export const TooltipProvider = (props: TooltipProviderProps) => {
  const [floatingElement, setFloatingElement] = useState<HTMLElement>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement>(null);
  const [referenceElement, setReferenceElement] = React.useState<Element>(null);
  const timerHandle = React.useRef<number>(null);

  useEffect(
    () => {
      const observer = new MutationObserver((mutations) => {
        if (!isNil(referenceElement)) {
          let activeTooltip = referenceElement.getAttribute('data-tooltip');
          if (Helpers.isBlank(activeTooltip)) {
            closeTooltip();
          }
        }

        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            closeTooltip();
          }

          if (mutation.type === 'attributes') {
            const newTooltip = (mutation.target as Element).getAttribute('data-tooltip');
            if (!Helpers.isBlank(newTooltip)) {
              showTooltip(mutation.target as Element);
            } else {
              closeTooltip();
            }
          }
        });
      });

      // Observe the entire document
      observer.observe(document.body, {
        childList: true,
        attributes: true,
        attributeFilter: ['data-tooltip'],
        subtree: true
      });

      return () => observer.disconnect();
    },
    [referenceElement]
  );

  useKeyCommands(
    {
      escape: () => {
        closeTooltip();
      }
    },
    floatingElement
  );

  // Per-anchor placement via the `data-tooltip-placement` attribute (set by Tooltip), defaulting to
  // 'top'. The fallback flips to the opposite side so the bubble stays on-screen.
  const requestedPlacement =
    (referenceElement?.getAttribute('data-tooltip-placement') as FloatingPlacement | null) ?? 'top';
  const oppositePlacement: Record<FloatingPlacement, FloatingPlacement> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left'
  };

  const {styles: floatingStyles, placement, isHidden}
    = useFloatingPosition(
    referenceElement,
    floatingElement,
    arrowElement,
    {
      placement: requestedPlacement,
      fallbackPlacements: [oppositePlacement[requestedPlacement]]
    }
  );

  // Promotes the tooltip into the browser's top layer, so it always paints above a
  // native <dialog> regardless of z-index or DOM position.
  useTopLayer(floatingElement);

  // Shrink the bubble to hug the wrapped text. A block that wraps still reserves its
  // full max-content width (= the single-line length, clamped to max-width), leaving
  // empty space either side of centred, wrapped lines. There is no CSS expression for
  // "the widest line after wrapping", so measure the text's per-line rects and pin the
  // width to the widest one. max-width and centring in CSS still apply; the floating
  // element's ResizeObserver re-centres the bubble once the width changes.
  useLayoutEffect(
    () => {
      if (isNil(floatingElement)) {
        return;
      }

      const textEl = floatingElement.querySelector(`.${styles.text}`) as HTMLElement | null;
      if (isNil(textEl)) {
        return;
      }

      // Clear any pinned width first so the text re-wraps naturally within max-width.
      textEl.style.width = '';

      const range = document.createRange();
      range.selectNodeContents(textEl);
      const lineRects = Array.from(range.getClientRects());

      let widestLine = 0;
      lineRects.forEach(rect => {
        widestLine = Math.max(widestLine, rect.width);
      });

      if (widestLine > 0) {
        textEl.style.width = `${Math.ceil(widestLine)}px`;
      }
    },
    [floatingElement, referenceElement]
  );

  const showTooltip = useCallback(
    (element: Element) => {
      if (referenceElement !== null) {
        setReferenceElement(element);
      } else {
        timerHandle.current = window.setTimeout(
          () => {
            setReferenceElement(element);
          },
          props.delayMilliseconds
        );
      }
    },
    [referenceElement, setReferenceElement]
  );

  const closeTooltip = useCallback(
    () => {
      window.clearTimeout(timerHandle.current);
      if (isNil(referenceElement)) {
        return;
      }

      referenceElement.removeAttribute('data-tooltip');
      setReferenceElement(null);
    },
    [timerHandle, referenceElement, setReferenceElement]
  );

  if (isNil(referenceElement)) {
    return null;
  }

  const tooltipText = referenceElement.getAttribute('data-tooltip');

  // Portal target: when a native <dialog> is open via showModal(), everything OUTSIDE
  // that dialog's DOM subtree is made inert by the browser - regardless of paint
  // order. Tooltips are not interactive, but portalling into the dialog keeps them
  // consistent with the other floating components and avoids being hidden from a11y.
  const portalTarget: Element = referenceElement.closest('dialog') ?? document.body;

  return ReactDOM.createPortal(
    <div
      ref={setFloatingElement}
      className={styles.popover}
      style={floatingStyles.floating}
      data-placement={placement}
      data-hidden={isHidden}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <span className={styles.text}>{tooltipText}</span>
      <div ref={setArrowElement} className={styles.arrow} style={floatingStyles.arrow}/>
    </div>,
    portalTarget
  );
};